import React, { useCallback, useEffect, useRef, useState } from "react";
import { EstimateBottomSheet } from "./estimate/EstimateBottomSheet";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { subscribeConnectivity } from "../helpers/connectivity";
import { subscribePictureSync } from "../helpers/pictureSyncStore";
import {
  formatSyncTime,
  readSyncStatus,
  requestSyncNow,
  syncStatusLabel,
  syncStatusLines,
} from "../helpers/syncStatus";
import "./SyncStatusIndicator.css";

function emptySnapshot() {
  return {
    offline: false,
    health: "synced",
    pendingCount: 0,
    attentionCount: 0,
    pictures: 0,
    notes: 0,
    timeEntries: 0,
    attentionMessage: "",
    lastSyncAt: "",
  };
}

export function SyncStatusIndicator({ actor, snapshot: snapshotOverride = null }) {
  const [liveSnapshot, setLiveSnapshot] = useState(emptySnapshot);
  const [open, setOpen] = useState(false);
  const actorRef = useRef(actor);
  actorRef.current = actor;
  const preview = snapshotOverride != null;
  const organizationId = actor?.organization_id || "";
  const userId = actor?.supabase_user_id || "";

  const refresh = useCallback(() => {
    if (preview) return () => {};
    let cancelled = false;
    readSyncStatus(actorRef.current).then((next) => {
      if (!cancelled) setLiveSnapshot(next);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [organizationId, userId, preview]);

  useEffect(() => {
    if (preview) return undefined;
    const cancel = refresh();
    const unsubConnectivity = subscribeConnectivity(() => refresh());
    const unsubPictures = subscribePictureSync(() => refresh());
    const onStatus = () => refresh();
    window.addEventListener("online", onStatus);
    window.addEventListener("offline", onStatus);
    window.addEventListener("crewpilot-sync-status", onStatus);
    return () => {
      cancel?.();
      unsubConnectivity();
      unsubPictures();
      window.removeEventListener("online", onStatus);
      window.removeEventListener("offline", onStatus);
      window.removeEventListener("crewpilot-sync-status", onStatus);
    };
  }, [preview, refresh]);

  const close = useCallback(() => setOpen(false), []);
  useHardwareBackHandler(useCallback(() => {
    if (!open) return false;
    close();
    return true;
  }, [open, close]), open);

  if (!actor || actor.isGuest) return null;

  const snapshot = preview ? snapshotOverride : liveSnapshot;
  const label = syncStatusLabel(snapshot);
  const tone = snapshot.attentionCount
    ? "attention"
    : snapshot.offline
      ? "offline"
      : label.startsWith("Syncing")
        ? "syncing"
        : "synced";
  const lines = syncStatusLines(snapshot);
  const lastSync = formatSyncTime(snapshot.lastSyncAt);

  return (
    <>
      <button
        type="button"
        className={`cp-sync-status cp-sync-status-${tone}`}
        data-testid="sync-status-indicator"
        aria-label={`Sync status: ${label}`}
        onClick={() => setOpen(true)}
      >
        <span className="cp-sync-status__dot" data-testid="sync-status-dot" aria-hidden="true" />
        <span className="cp-sync-status__label">{label}</span>
      </button>
      {open && (
        <EstimateBottomSheet
          title="Sync status"
          onClose={close}
          secondaryLabel="Close"
          onSecondary={close}
          primaryLabel="Sync now"
          onPrimary={() => {
            requestSyncNow();
            close();
          }}
        >
          <div className="cp-sync-sheet" data-testid="sync-status-sheet">
            <div className="cp-sync-sheet-line">{snapshot.offline ? "Offline" : "Online"}</div>
            {lines.length > 0 && <div className="cp-sync-sheet-label">Pending</div>}
            {lines.map((line) => (
              <div key={line} className="cp-sync-sheet-line">{line}</div>
            ))}
            {snapshot.attentionCount > 0 && (
              <>
                <div className="cp-sync-sheet-label">Needs attention</div>
                <div className="cp-sync-sheet-line">
                  {snapshot.attentionMessage || `${snapshot.attentionCount} need attention`}
                </div>
              </>
            )}
            {lastSync && (
              <>
                <div className="cp-sync-sheet-label">Last successful sync</div>
                <div className="cp-sync-sheet-line">{lastSync}</div>
              </>
            )}
          </div>
        </EstimateBottomSheet>
      )}
    </>
  );
}
